/** Referrals page: the offer and your link up front, friend milestones beside the friends list, then earnings by level with the calculator behind a row. */
import { useMemo, useState } from 'react';
import { BRAND, fmtUsd, QUESTS, REFERRAL_CODE_RE, type QuestState, type ReferralsOverview } from '@platform/shared';
import { Glyph } from '../../components/NotificationIcon';
import { cx, Icon, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { referralLink } from '../../lib/referral';
import { CopyButton, errMsg, PillButton, QrCode, when } from '../account/kit';
import { btn, EarnBadge, fmtPct, fmtPts, HeroPanel, Panel, type Msg } from './shared';

const usd0 = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const VOLUMES = [5_000, 10_000, 25_000, 50_000, 100_000, 250_000, 500_000, 1_000_000];
const short = (n: number) => (n >= 1_000_000 ? `$${n / 1_000_000}M` : `$${n / 1000}K`);

/** "How much can I make?": friends × monthly volume → one-time bonuses and monthly fee share. */
function Calculator({ data }: { data: ReferralsOverview }) {
  const { bonusUsd, levelPcts } = data.program.settings.referral;
  const fee = data.program.avgFeeRate;
  const [friends, setFriends] = useState(5);
  const [volIdx, setVolIdx] = useState(4);
  const vol = VOLUMES[volIdx]!;
  const est = useMemo(() => {
    const bonus = friends * bonusUsd;
    const monthly = friends * vol * fee * (levelPcts[0] / 100);
    return { bonus, monthly, year: bonus + monthly * 12 };
  }, [friends, vol, fee, bonusUsd, levelPcts]);
  return (
    <div className="grid gap-5 rounded-[16px] border border-line bg-card/70 p-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] md:p-5">
      <div className="flex min-w-0 flex-col gap-5">
        <div>
          <b className="text-[16px]">How much can you make?</b>
          <p className="m-0 mt-1 text-[13px] text-muted">Move the sliders to estimate your rewards.</p>
        </div>
        <label className="flex flex-col gap-2">
          <span className="flex justify-between text-[13px] text-muted">
            Friends who join and trade <b className="num text-fg">{friends}</b>
          </span>
          <input id="calc-friends" type="range" min={1} max={50} value={friends} onChange={(e) => setFriends(Number(e.target.value))} className="w-full accent-[var(--accent)]" />
        </label>
        <label className="flex flex-col gap-2">
          <span className="flex justify-between text-[13px] text-muted">
            Each friend trades per month <b className="num text-fg">{short(vol)}</b>
          </span>
          <input id="calc-volume" type="range" min={0} max={VOLUMES.length - 1} value={volIdx} onChange={(e) => setVolIdx(Number(e.target.value))} className="w-full accent-[var(--accent)]" />
        </label>
      </div>
      <div className="flex min-w-0 flex-col justify-between gap-4 rounded-[14px] bg-track p-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <div className="text-[12px] text-muted">Sign-up rewards</div>
            <div className="num text-[20px] font-semibold">{usd0(est.bonus)}</div>
            <div className="text-[11.5px] text-muted">one time</div>
          </div>
          <div>
            <div className="text-[12px] text-muted">Your share of their fees</div>
            <div className="num text-[20px] font-semibold">{usd0(est.monthly)}</div>
            <div className="text-[11.5px] text-muted">every month</div>
          </div>
        </div>
        <div className="border-t border-line pt-3">
          <div className="text-[12px] text-muted">Estimated first year</div>
          <div className="num text-[30px] font-semibold text-up">{usd0(est.year)}</div>
        </div>
        <p className="m-0 text-[11.5px] leading-snug text-muted">
          Estimate only, using an average trading fee of {fmtPct(fee * 100)} and your level 1 share of {fmtPct(levelPcts[0])}. Earnings from levels 2 and 3 come on top.
        </p>
      </div>
    </div>
  );
}

export function InviteModal({ open, onClose, code, bonus }: { open: boolean; onClose: () => void; code: string; bonus: number }) {
  const link = referralLink(code);
  const text = `Join me on ${BRAND.name}: ${link}`;
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  return (
    <Modal open={open} onClose={onClose} title="Invite friends" width={440}>
      <div className="flex flex-col gap-4 text-[14px]">
        <p className="m-0 text-muted">Send your friend this link. Your code is filled in for them when they sign up, and you get {fmtUsd(String(bonus))} once they're verified and make their first trade.</p>
        <div className="flex flex-col gap-1.5">
          <span className="pl-5 text-[12px] text-muted">Your referral link</span>
          <div className="flex min-w-0 items-center gap-2 rounded-full border border-line bg-bg py-1.5 pr-1.5 pl-5">
            <span className="num min-w-0 flex-1 truncate text-[13px]" title={link}>
              {link}
            </span>
            <CopyButton value={link} kind="primary" size="sm">
              Copy link
            </CopyButton>
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 rounded-[16px] border border-line bg-bg px-5 py-3">
          <div>
            <div className="text-[12px] text-muted">Your referral code</div>
            <div className="num text-[22px] font-semibold tracking-wider">{code}</div>
          </div>
          <span className="text-right text-[12px] text-muted">Made for you automatically</span>
        </div>
        <div className={cx('grid gap-2', canShare && 'grid-cols-2')}>
          <CopyButton value={text} kind="secondary" size="sm">
            Copy message
          </CopyButton>
          {canShare && (
            <PillButton kind="secondary" size="sm" onClick={() => void navigator.share({ title: BRAND.name, text: `Join me on ${BRAND.name}`, url: link }).catch(() => {})}>
              Share…
            </PillButton>
          )}
        </div>
      </div>
    </Modal>
  );
}

export function ApplyCodeModal({ open, onClose, days, onDone }: { open: boolean; onClose: () => void; days: number; onDone: (m: Msg) => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <Modal open={open} onClose={onClose} title="Set referral code" width={400}>
      <form
        className="flex flex-col gap-3"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            await api.post('/api/referrals/apply', { code: code.trim() });
            onDone({ ok: true, text: 'Referral code applied.' });
            onClose();
          } catch (ex) {
            setErr(errMsg(ex));
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="m-0 text-[14px] text-muted">Enter the code of the friend who invited you. You can do this once, within {days} days of signing up.</p>
        <input id="apply-code" autoFocus value={code} onChange={(e) => setCode(e.target.value)} maxLength={16} placeholder="Referral code" aria-label="Referral code" className="num h-12 rounded-full border border-line bg-bg px-5 text-[16px] text-fg outline-none focus:border-line-strong" />
        {err && (
          <span role="alert" className="pl-5 text-[13px] text-down">
            {err}
          </span>
        )}
        <PillButton type="submit" size="sm" busy={busy} disabled={!REFERRAL_CODE_RE.test(code.trim())}>
          Apply code
        </PillButton>
      </form>
    </Modal>
  );
}

const SHARE_TO = [
  { label: 'WhatsApp', url: (t: string) => `https://wa.me/?text=${encodeURIComponent(t)}` },
  { label: 'Telegram', url: (t: string, l: string) => `https://t.me/share/url?url=${encodeURIComponent(l)}&text=${encodeURIComponent(t)}` },
  { label: 'X', url: (t: string) => `https://x.com/intent/post?text=${encodeURIComponent(t)}` },
  { label: 'Email', url: (t: string) => `mailto:?subject=${encodeURIComponent(`Join me on ${BRAND.name}`)}&body=${encodeURIComponent(t)}` },
] as const;

interface Rung {
  key: string;
  target: number;
  title: string;
  reward: string;
  done: boolean;
  count: number;
  unit: string;
  cash?: boolean;
}

/** First row: the offer with Invite and Copy link on a glowing card, and your link, share buttons, code and how your friends are getting on beside it. */
export function ReferralBox({ data, onInvite }: { data: ReferralsOverview; onInvite: () => void }) {
  const { bonusUsd, levelPcts } = data.program.settings.referral;
  const link = referralLink(data.code);
  const text = `Join me on ${BRAND.name}: ${link}`;
  const [qr, setQr] = useState(false);
  const { joined, verified, traded } = data.funnel;
  const notTraded = verified - traded;
  return (
    <div className="grid gap-4 md:gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
      <HeroPanel glow="50% 120%" className="!gap-4">
        <div className="flex items-center justify-between gap-3">
          <EarnBadge glyph="invite" tone="next" size="lg" />
          <span className="num rounded-lg bg-accent-bg px-2.5 py-1 text-[12.5px] font-semibold text-accent-fg">{usd0(bonusUsd)} per friend</span>
        </div>
        <h2 className="m-0 mt-2 text-[28px] leading-tight font-semibold tracking-tight md:text-[36px]">Invite a friend, get {usd0(bonusUsd)}</h2>
        <p className="m-0 text-[15px] text-muted">
          Paid to your live account when they verify and make a first trade. Then you keep <b className="text-fg">{fmtPct(levelPcts[0])} of their fees</b>, plus {fmtPct(levelPcts[1])} and {fmtPct(levelPcts[2])} from the people they invite.
        </p>
        <div className="mt-auto flex flex-col gap-2.5 pt-2">
          <button type="button" onClick={onInvite} className={cx(btn('primary', 'lg'), 'w-full')}>
            Invite friends <Glyph name="invite" size={16} />
          </button>
          <CopyButton value={link} kind="secondary" size="sm" className={cx(btn('secondary', 'lg'), '!h-12 w-full !rounded-xl !bg-transparent !text-[15px]')}>
            Copy link <Glyph name="copy" size={16} />
          </CopyButton>
        </div>
      </HeroPanel>

      <Panel>
        <div className="flex flex-col gap-1.5">
          <span className="text-[12.5px] text-muted">Your referral link</span>
          <div className="flex min-w-0 items-center gap-2 rounded-xl border border-line-strong bg-bg py-1.5 pr-1.5 pl-4">
            <span className="num min-w-0 flex-1 truncate text-[13.5px]" title={link}>
              {link}
            </span>
            <CopyButton value={link} kind="secondary" size="sm" className="!h-8 !rounded-lg !bg-accent !px-4 !text-[13px] !text-white">
              Copy
            </CopyButton>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {SHARE_TO.map((x) => (
            <a key={x.label} href={x.url(text, link)} target="_blank" rel="noreferrer" className="flex h-9 items-center rounded-lg bg-track px-4 text-[13px] font-semibold text-fg no-underline">
              {x.label}
            </a>
          ))}
          <button type="button" aria-expanded={qr} onClick={() => setQr((v) => !v)} className="h-9 rounded-lg bg-track px-4 text-[13px] font-semibold text-fg">
            QR code
          </button>
        </div>
        {qr && (
          <div className="self-start rounded-2xl bg-white p-3">
            <QrCode value={link} size={160} label="QR code for your referral link" />
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-bg/40 px-4 py-3">
          <div>
            <div className="text-[12.5px] text-muted">Or share your code</div>
            <div className="num text-[22px] font-semibold tracking-[0.12em]">{data.code}</div>
          </div>
          <CopyButton value={data.code} kind="secondary" size="sm" className="!h-8 !rounded-lg !border !border-line-strong !bg-transparent !px-4 !text-[13px] !text-fg">
            Copy code
          </CopyButton>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {(
            [
              ['invite', String(joined), 'Joined'],
              ['shieldCheck', String(verified), 'Verified'],
              ['candles', String(traded), 'Traded'],
              ['wallet', usd0(Number(data.totals.bonuses)), 'Rewards earned'],
            ] as const
          ).map(([g, v, l], i) => (
            <div key={l} className={cx('flex flex-col gap-1.5 rounded-2xl border p-3.5', i === 3 ? 'border-up/40 bg-up-bg' : 'border-line')}>
              <EarnBadge glyph={g} tone={i === 3 ? 'done' : 'base'} size="xs" />
              <span className="num text-[21px] font-semibold">{v}</span>
              <span className="text-[12px] text-muted">{l}</span>
            </div>
          ))}
        </div>
        {notTraded > 0 && (
          <span className="text-[12.5px] text-muted">
            {notTraded} {notTraded === 1 ? 'friend is' : 'friends are'} verified and {notTraded === 1 ? "hasn't" : "haven't"} traded yet. Your {usd0(bonusUsd)} arrives after their first trade.
          </span>
        )}
      </Panel>
    </div>
  );
}

/** The invite quests (points, counted by friends who joined), then the cash milestones (counted by friends whose reward was earned), and what happened lately. */
export function FriendMilestones({ data, quests }: { data: ReferralsOverview; quests: QuestState[] }) {
  const { bonusUsd } = data.program.settings.referral;
  const qp = data.program.settings.questPoints;
  const joined = data.funnel.joined;
  const ladder: Rung[] = [
    ...QUESTS.filter((q) => q.id === 'refer_1' || q.id === 'refer_5').map((q) => ({
      key: q.id,
      target: q.target!,
      title: q.title,
      reward: `+${fmtPts(qp[q.id] ?? q.points)} pts`,
      done: !!quests.find((s) => s.id === q.id)?.done,
      count: joined,
      unit: 'to go',
    })),
    ...data.milestones.map((m) => ({
      key: `cash_${m.friends}`,
      target: m.friends,
      title: `${m.friends} friends rewarded`,
      reward: `+${usd0(m.usd)}`,
      done: m.reached,
      count: data.rewarded,
      unit: 'more rewarded friends',
      cash: true,
    })),
  ];
  const next = ladder.find((r) => !r.done);
  return (
    <Panel title="Friend milestones" icon="trophy" aside={<span className="num text-[12.5px] text-muted">{joined} joined</span>}>
      <ol className="m-0 flex list-none flex-col p-0">
        {ladder.map((r) => {
          const go = next?.key === r.key;
          return (
            <li key={r.key} className="grid grid-cols-[34px_minmax(0,1fr)_auto] items-center gap-3 border-t border-line py-2.5 first:border-t-0">
              <span className={cx('num grid size-[34px] place-items-center rounded-full text-[12px] font-semibold', r.done ? 'bg-up text-bg' : go ? 'bg-accent text-white' : 'bg-track text-muted')}>
                {r.done ? <Glyph name="check" size={16} /> : r.target}
              </span>
              <span className="min-w-0 text-[14px] font-semibold">
                {r.title}
                {go && (
                  <small className="block text-[12.5px] font-normal text-accent-fg">
                    {Math.max(0, r.target - r.count)} {r.unit}
                  </small>
                )}
              </span>
              <span className={cx('num rounded-md px-2 py-0.5 text-[12px] font-semibold', r.cash ? 'bg-up-bg text-up' : 'bg-accent-bg text-accent-fg')}>{r.reward}</span>
            </li>
          );
        })}
      </ol>
      {next && (
        <div aria-hidden className="h-1.5 overflow-hidden rounded-full bg-track">
          <div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(100, (next.count / next.target) * 100)}%` }} />
        </div>
      )}
      {data.milestones.length > 0 && <span className="text-[12.5px] text-muted">Cash milestones count friends whose {usd0(bonusUsd)} reward was paid, and go to your live account like any referral reward.</span>}
      <div className="flex flex-col border-t border-line pt-3">
        <span className="text-[11px] font-semibold tracking-[0.06em] text-muted uppercase">Recent</span>
        {data.recent.length === 0 ? (
          <span className="py-2 text-[13px] text-muted">When a friend joins or makes a first trade, you'll see it here.</span>
        ) : (
          data.recent.map((e, i) => (
            <div key={i} className="flex items-center gap-2.5 py-2 text-[13.5px]">
              <EarnBadge glyph={e.kind === 'traded' ? 'check' : 'invite'} tone={e.kind === 'traded' ? 'done' : 'accent'} size="xs" />
              <span className="min-w-0 flex-1 truncate">
                {e.username} {e.kind === 'traded' ? 'made a first trade' : 'joined with your link'}
                {e.bonus && <b className="text-up"> · +{usd0(Number(e.bonus))}</b>}
              </span>
              <span className="shrink-0 text-[12px] text-muted">{when(e.at).split(',')[0]}</span>
            </div>
          ))
        )}
      </div>
    </Panel>
  );
}

/** Earnings per level, and the calculator behind a row that opens it. */
export function ReferralLevels({ data }: { data: ReferralsOverview }) {
  const [calc, setCalc] = useState(false);
  return (
    <Panel title="Earnings by level" icon="chart" aside={<span className="num text-[12.5px] text-muted">{fmtUsd(data.totals.earned)} earned in total</span>}>
      <div className="grid gap-2.5 sm:grid-cols-3">
        {data.levels.map((l) => (
          <div key={l.level} className="flex items-center gap-3 rounded-2xl border border-line px-4 py-3.5">
            <EarnBadge tone={l.level === 1 ? 'accent' : 'base'}>L{l.level}</EarnBadge>
            <div className="min-w-0">
              <div className="text-[12.5px] text-muted">
                Level {l.level} · {fmtPct(l.pct)} · {l.users} {l.users === 1 ? 'user' : 'users'}
              </div>
              <div className="num text-[16px] font-semibold">{fmtUsd(l.earned)}</div>
            </div>
          </div>
        ))}
      </div>
      <button type="button" aria-expanded={calc} onClick={() => setCalc((v) => !v)} className="flex w-full items-center gap-3 rounded-2xl border border-line px-4 py-3.5 text-left hover:bg-track">
        <EarnBadge glyph="wallet" tone="accent" size="sm" />
        <span className="min-w-0 flex-1">
          <span className="block text-[14.5px] font-semibold">How much can you make?</span>
          <span className="block text-[12.5px] text-muted">{calc ? 'Close the calculator' : 'Open the calculator: friends × monthly volume → sign-up rewards and fee share'}</span>
        </span>
        <Icon name="chevron" size={16} strokeWidth={2.4} className={cx('shrink-0 text-muted transition-transform', calc && 'rotate-180')} />
      </button>
      {calc && <Calculator data={data} />}
    </Panel>
  );
}

const initials = (u: string) => (u.match(/[A-Z]/g)?.slice(0, 2).join('') || u.slice(0, 2)).toUpperCase();

/** Each friend as a notification-style row: who, when and what level, where their reward stands, their volume and the fees you earned. */
export function ReferralTable({ data, onApply }: { data: ReferralsOverview; onApply?: () => void }) {
  const [level, setLevel] = useState<0 | 1 | 2 | 3>(0);
  const rows = data.referees.filter((r) => level === 0 || r.level === level);
  const bonus = data.program.settings.referral.bonusUsd;
  return (
    <Panel
      title={
        <>
          Your friends <span className="num ml-1 rounded-md bg-track px-2 py-0.5 align-middle text-[12px] font-medium text-muted">{data.totals.users}</span>
        </>
      }
      icon="user"
      aside={
        <div role="radiogroup" aria-label="Level" className="flex gap-0.5 rounded-[10px] bg-track p-[3px]">
          {([0, 1, 2, 3] as const).map((lv) => (
            <button
              key={lv}
              type="button"
              role="radio"
              aria-checked={level === lv}
              onClick={() => setLevel(lv)}
              className={cx('h-[30px] rounded-lg px-3 text-[12.5px] font-semibold', level === lv ? 'bg-card text-fg' : 'text-muted hover:text-fg')}
            >
              {lv === 0 ? 'All' : `L${lv}`}
            </button>
          ))}
        </div>
      }
    >
      {rows.length === 0 ? (
        <div className="py-10 text-center text-[15px] text-muted">No friends yet. Invite a friend to earn {usd0(bonus)}.</div>
      ) : (
        <div className="-mx-3 flex flex-col gap-0.5">
          {rows.map((r, i) => {
            const tag =
              r.bonus === 'paid'
                ? { text: 'Earned', cls: 'bg-up-bg text-up', tip: undefined }
                : r.bonus === 'waiting'
                  ? r.waitingFor === 'review'
                    ? { text: 'Being checked', cls: 'bg-accent-bg text-accent-fg', tip: "We're checking this reward. It's paid once approved." }
                    : r.waitingFor === 'first_trade'
                      ? { text: 'Needs first trade', cls: 'bg-warn-bg text-warn', tip: 'Paid after their first live trade' }
                      : { text: 'Needs verification', cls: 'bg-track text-muted', tip: "Paid once they're verified and make a first live trade" }
                  : r.bonus === 'rejected'
                    ? { text: 'Not eligible', cls: 'bg-down-bg text-down', tip: "This friend's sign-up didn't meet the referral terms" }
                    : null;
            return (
              <div key={i} className="flex items-center gap-3 rounded-xl p-3">
                <span aria-hidden className={cx('grid size-10 shrink-0 place-items-center rounded-full text-[13px] font-semibold', r.active ? 'bg-up-bg text-up' : 'bg-track text-muted')}>
                  {initials(r.username)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14.5px] font-semibold">{r.username}</div>
                  <div className="text-[12.5px] text-muted">
                    Joined {when(r.joinedAt, true).split(',')[0]} · L{r.level} · {r.active ? 'trading' : 'not trading yet'}
                  </div>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1 text-right">
                  {tag ? (
                    <span title={tag.tip} className={cx('rounded-md px-2 py-0.5 text-[12px] font-semibold whitespace-nowrap', tag.cls)}>
                      {tag.text}
                    </span>
                  ) : (
                    <span className="text-[12px] text-muted">Level 1 only</span>
                  )}
                  <span className="num text-[12px] text-muted">
                    {fmtUsd(r.volume)} volume · <b className="font-semibold text-fg">{fmtUsd(r.earned)}</b> fees
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {onApply && (
        <button type="button" onClick={onApply} className="self-start text-[13px] font-semibold text-accent-fg hover:text-fg">
          Enter the code of the friend who invited you
        </button>
      )}
    </Panel>
  );
}
